import React from 'react';
import { useTranslation } from 'react-i18next';
import { HelpCircle } from 'lucide-react';
import { Button } from '../ui/button';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../ui/accordion';

const FAQSection = () => {
  const { i18n } = useTranslation();
  const isArabic = i18n.language === 'ar';

  const faqData = [
    {
      id: 'item-1',
      questionAr: 'ما هي الخدمات التي تقدمها وازن؟',
      questionEn: 'What services does Wazin provide?',
      answerAr: 'تقدم وازن الاستشارات القانونية، وصياغة العقود، وإدارة القضايا، والتمثيل القضائي، وخدمات قانونية متخصصة للأفراد وقطاع الأعمال.',
      answerEn: 'Wazin provides legal consultations, contract drafting, case management, litigation representation, and specialized legal services for individuals and businesses.'
    },
    {
      id: 'item-2',
      questionAr: 'هل تقدم وازن خدمات للشركات والأفراد؟',
      questionEn: 'Does Wazin serve both companies and individuals?',
      answerAr: 'نعم، نقدم خدماتنا للأفراد والمنشآت والشركات وفق طبيعة الاحتياج ونطاق الخدمة المناسب لكل حالة.',
      answerEn: 'Yes. We serve individuals, establishments, and companies according to the nature of the need and the proper service scope.'
    },
    {
      id: 'item-3',
      questionAr: 'كيف أبدأ التعامل مع وازن؟',
      questionEn: 'How do I start working with Wazin?',
      answerAr: 'يمكنك البدء بطلب الخدمة أو حجز استشارة، وسيقوم الفريق بمراجعة الطلب وتحديد المسار الأنسب للتعامل.',
      answerEn: 'You can begin by requesting a service or booking a consultation, and our team will review your request and define the best path forward.'
    },
    {
      id: 'item-4',
      questionAr: 'هل يمكن إدارة القضايا بشكل كامل؟',
      questionEn: 'Can cases be fully managed through Wazin?',
      answerAr: 'نعم، نوفر إدارة ومتابعة شاملة للقضايا مع تحديثات منتظمة ومتابعة دقيقة لكل مرحلة من مراحل العمل.',
      answerEn: 'Yes. We provide full case management and follow-up, with regular updates and close attention to each stage.'
    },
    {
      id: 'item-5',
      questionAr: 'هل التعاقد يتم إلكترونيًا؟',
      questionEn: 'Is contracting done electronically?',
      answerAr: 'نعم، يمكن إتمام التعاقد إلكترونيًا بطريقة منظمة وآمنة بما يسهّل بدء الخدمة بسرعة ووضوح.',
      answerEn: 'Yes. Contracting can be completed electronically in an organized and secure way to start the service quickly and clearly.'
    },
    {
      id: 'item-6',
      questionAr: 'كم تستغرق مدة الرد على الطلبات؟',
      questionEn: 'How long does it take to respond to requests?',
      answerAr: 'يعمل فريق وازن على مراجعة الطلبات والرد عليها بسرعة، مع تحديد الخطوة التالية بوضوح.',
      answerEn: 'Wazin reviews incoming requests promptly and clarifies the next step without delay.'
    },
    {
      id: 'item-7',
      questionAr: 'هل يمكن متابعة القضايا والمستندات رقميًا؟',
      questionEn: 'Can cases and documents be followed digitally?',
      answerAr: 'نعم، يمكن متابعة الملفات والمستندات والخدمات القانونية عبر القنوات والمنصات المعتمدة.',
      answerEn: 'Yes. Cases, documents, and legal services can be followed through approved digital channels and platforms.'
    },
    {
      id: 'item-8',
      questionAr: 'هل توفر وازن حلولاً قانونية لقطاع الأعمال؟',
      questionEn: 'Does Wazin provide legal solutions for businesses?',
      answerAr: 'نعم، تقدم وازن حلولاً قانونية مرنة للمنشآت والشركات تشمل العقود، القضايا، والاستشارات المتخصصة.',
      answerEn: 'Yes. Wazin provides flexible legal solutions for establishments and companies, including contracts, disputes, and specialized consultations.'
    }
  ];

  return (
    <section id="faq" className="py-20 px-4 bg-white relative z-10">
      <div className="max-w-5xl mx-auto">
        <div className="text-center mb-12" dir={isArabic ? 'rtl' : 'ltr'}>
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-gradient-to-br from-[#B58C5A] via-[#A27849] to-[#8C653A] shadow-md mb-5">
            <HelpCircle className="w-8 h-8 text-white" />
          </div>

          <h2 className="text-3xl md:text-4xl font-bold text-wazin-navy mb-4">
            {isArabic ? 'أسئلة تساعدك تبدأ بثقة' : 'Questions That Help You Start with Confidence'}
          </h2>

          <p className="text-lg text-gray-600 max-w-2xl mx-auto leading-relaxed">
            {isArabic
              ? 'إجابات مختصرة على أكثر الأسئلة التي تساعدك على فهم طريقة العمل مع وازن.'
              : 'Brief answers to common questions that help you understand how working with Wazin begins.'}
          </p>
        </div>

        <div className="rounded-[28px] border border-[#A27849]/15 bg-white shadow-[0_18px_45px_rgba(15,23,42,0.06)] overflow-hidden">
          <Accordion type="single" collapsible defaultValue="item-1" className="w-full">
            {faqData.map((faq, index) => {
              const isGold = index % 2 === 0;

              return (
                <AccordionItem
                  key={faq.id}
                  value={faq.id}
                  className={`border-b last:border-b-0 transition-all duration-300 data-[state=open]:scale-[1.01] ${
                    isGold
                      ? `${isArabic ? 'border-r-4' : 'border-l-4'} border-[#A27849] data-[state=open]:bg-[#faf8f3]`
                      : `${isArabic ? 'border-r-4' : 'border-l-4'} border-[#1F3B64]/80 data-[state=open]:bg-[#f8fafc]`
                  }`}
                >
                  <AccordionTrigger
                    className={`px-6 md:px-8 py-5 hover:no-underline transition-all duration-300 ${
                      isGold ? 'hover:bg-[#faf8f3]' : 'hover:bg-[#f8fafc]'
                    } ${isArabic ? 'text-right flex-row-reverse' : 'text-left'}`}
                  >
                    <div className={`flex items-start flex-1 gap-4 ${isArabic ? 'flex-row-reverse text-right' : 'text-left'}`}>
                      <span
                        className={`w-9 h-9 rounded-full flex items-center justify-center text-sm font-bold flex-shrink-0 mt-0.5 ${
                          isGold
                            ? 'bg-[#A27849]/12 text-[#8C653A]'
                            : 'bg-[#1F3B64]/10 text-[#1F3B64]'
                        }`}
                      >
                        {String(index + 1).padStart(2, '0')}
                      </span>

                      <span className={`text-base md:text-lg font-semibold text-wazin-navy leading-8 flex-1 ${isArabic ? 'text-right' : 'text-left'}`}>
                        {isArabic ? faq.questionAr : faq.questionEn}
                      </span>
                    </div>
                  </AccordionTrigger>

                  <AccordionContent className={`px-6 md:px-8 ${isArabic ? 'text-right' : 'text-left'}`}>
                    <div className={`${isArabic ? 'pr-[52px] text-right' : 'pl-[52px] text-left'} pb-5`}>
                      <p className="text-gray-600 text-base leading-8 max-w-3xl">
                        {isArabic ? faq.answerAr : faq.answerEn}
                      </p>
                    </div>
                  </AccordionContent>
                </AccordionItem>
              );
            })}
          </Accordion>
        </div>

        <div className="text-center mt-10">
          <Button
            asChild
            className="bg-gradient-to-r from-[#B58C5A] via-[#A27849] to-[#8C653A] text-white px-8 py-6 text-lg font-bold hover:shadow-lg transition-all duration-300"
          >
            <a href="/#locations">
              {isArabic ? 'لم تجد سؤالك؟ تواصل معنا' : "Didn't find your question? Contact us"}
            </a>
          </Button>
        </div>
      </div>
    </section>
  );
};

export default FAQSection;
